import { z } from 'zod';

/**
 * Retail Media RM-9: Werbemittel der Bannerformate (Vorgabe docs/retail-media-formate.md).
 *
 * marke    Sponsored Brand Ads: Logo, Headline, bis zu drei Produkte, Ziel (Markenseite/Kollektion)
 * display  Display Ads: Bild Desktop + Bild Mobil, Headline, Text, CTA, Ziel
 *
 * Ausgespielt wird ein Werbemittel erst nach der Freigabe durch den Marktplatz. Jede Änderung eines eingereichten,
 * freigegebenen oder abgelehnten Werbemittels setzt den Status zurück auf „pruefung“; ein Entwurf bleibt Entwurf,
 * bis er eingereicht wird. Bilder liegen in werbung_bild und sind öffentlich unter /v1/rm/bild/<id> abrufbar.
 */

export const WERBEMITTEL_ARTEN = { marke: 'Markenbanner', display: 'Display-Banner' } as const;
export type WerbemittelArt = keyof typeof WERBEMITTEL_ARTEN;
const ARTEN = ['marke', 'display'] as const;

export const WERBEMITTEL_STATUS = { entwurf: 'Entwurf', pruefung: 'In Prüfung', freigegeben: 'Freigegeben', abgelehnt: 'Abgelehnt' } as const;
/** Wer freigibt: Standard der Marktplatz; vom Marktplatz gestaltete WKZ-Werbemittel gibt der Werbetreibende frei */
export type WerbemittelFreigabeDurch = 'marktplatz' | 'werbetreibender';
export type WerbemittelStatus = keyof typeof WERBEMITTEL_STATUS;
const STATUS = ['entwurf', 'pruefung', 'freigegeben', 'abgelehnt'] as const;

export const WERBEMITTEL_GRENZEN = {
  headline: 80,
  text: 200,
  cta: 30,
  zielUrl: 2000,
  grund: 500,
  /** höchstens so viele Produkte je Art */
  produkte: { marke: 3, display: 0 } as Record<WerbemittelArt, number>,
} as const;

/* ------------------------------------------------------------------ Bilder */

export const WERBUNG_BILD_TYPEN = ['image/png', 'image/jpeg', 'image/webp'] as const;
/** 1,5 MB – verkleinert wird im Browser wie beim Branding */
export const WERBUNG_BILD_MAX_BYTES = 1_572_864;
/** längste Seite höchstens */
export const WERBUNG_BILD_MAX_KANTE = 4_000;

/** Verwendung eines Bildes mit Mindestmaßen (Pixel) */
export const WERBUNG_BILD_VERWENDUNGEN = {
  logo: { name: 'Logo', minBreite: 100, minHoehe: 40 },
  desktop: { name: 'Bild Desktop', minBreite: 970, minHoehe: 250 },
  mobil: { name: 'Bild Mobil', minBreite: 320, minHoehe: 100 },
} as const;
export type WerbungBildVerwendung = keyof typeof WERBUNG_BILD_VERWENDUNGEN;

/** Mindestmaße als Text, z. B. „mindestens 970 × 250 px“ */
export const werbungBildMasseText = (v: WerbungBildVerwendung) =>
  `mindestens ${WERBUNG_BILD_VERWENDUNGEN[v].minBreite} × ${WERBUNG_BILD_VERWENDUNGEN[v].minHoehe} px`;

/**
 * Kleinster Verkleinerungsfaktor, bei dem das Bild die Mindestmaße der Verwendung noch erfüllt.
 * Größer als 1: Das Bild ist zu klein, auch unverändert.
 */
export function werbungBildMindestFaktor(breite: number, hoehe: number, v: WerbungBildVerwendung): number {
  const m = WERBUNG_BILD_VERWENDUNGEN[v];
  if (breite <= 0 || hoehe <= 0) return Infinity;
  return Math.max(m.minBreite / breite, m.minHoehe / hoehe);
}

/**
 * Zielmaße beim Verkleinern im Browser: längste Seite höchstens WERBUNG_BILD_MAX_KANTE, `wunsch` verkleinert weiter
 * (z. B. wenn die Datei noch zu groß ist), aber nie unter die Mindestmaße. null: Bild zu klein – oder so schmal,
 * dass es mit höchstens WERBUNG_BILD_MAX_KANTE an der längsten Seite die Mindestmaße nicht erreicht.
 */
export function werbungBildZielmasse(
  breite: number,
  hoehe: number,
  v: WerbungBildVerwendung,
  wunsch = 1,
): { breite: number; hoehe: number; faktor: number } | null {
  const mindest = werbungBildMindestFaktor(breite, hoehe, v);
  if (mindest > 1) return null;
  const kante = Math.min(1, WERBUNG_BILD_MAX_KANTE / Math.max(breite, hoehe));
  if (kante < mindest) return null;
  const faktor = Math.max(mindest, Math.min(kante, wunsch));
  const m = WERBUNG_BILD_VERWENDUNGEN[v];
  // Rundung darf weder die Mindestmaße unterschreiten noch die längste Seite überschreiten
  const runden = (x: number, min: number) => Math.min(WERBUNG_BILD_MAX_KANTE, Math.max(min, Math.round(x * faktor)));
  return { breite: runden(breite, m.minBreite), hoehe: runden(hoehe, m.minHoehe), faktor };
}

/** Bildfelder des Werbemittels und ihre Verwendung */
export const WERBEMITTEL_BILDFELDER = {
  logoBildId: 'logo',
  bildDesktopId: 'desktop',
  bildMobilId: 'mobil',
} as const satisfies Record<string, WerbungBildVerwendung>;
export type WerbemittelBildfeld = keyof typeof WERBEMITTEL_BILDFELDER;

/** Öffentlicher Pfad eines Werbebilds (relativ zur Adresse der Plattform) */
export const werbungBildPfad = (id: string) => `/v1/rm/bild/${id}`;

/* ------------------------------------------------------------------ Adressen */

/** Ziel eines Werbemittels: https-Adresse oder Pfad im Shop (/collections/…). Nie javascript:, data: oder //host. */
export function zielUrlGueltig(s: string): boolean {
  if (!s || /[\s\\<>"'`]/.test(s) || /[\u0000-\u001f\u007f]/.test(s)) return false;
  if (s.startsWith('/')) return !s.startsWith('//');
  try {
    const u = new URL(s);
    return u.protocol === 'https:' && !!u.hostname && !u.username && !u.password;
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------ Eingaben */

const BildId = z.string().uuid().nullable().default(null);

export const WerbemittelFelder = z.object({
  name: z.string().trim().min(2, 'Bitte Namen angeben').max(120, 'Höchstens 120 Zeichen'),
  headline: z.string().trim().max(WERBEMITTEL_GRENZEN.headline, `Höchstens ${WERBEMITTEL_GRENZEN.headline} Zeichen`).default(''),
  text: z.string().trim().max(WERBEMITTEL_GRENZEN.text, `Höchstens ${WERBEMITTEL_GRENZEN.text} Zeichen`).default(''),
  cta: z.string().trim().max(WERBEMITTEL_GRENZEN.cta, `Höchstens ${WERBEMITTEL_GRENZEN.cta} Zeichen`).default(''),
  zielUrl: z.string().trim().max(WERBEMITTEL_GRENZEN.zielUrl, 'Adresse zu lang').default('')
    .refine((s) => s === '' || zielUrlGueltig(s), 'Bitte eine https-Adresse oder einen Pfad wie /collections/… angeben'),
  logoBildId: BildId,
  bildDesktopId: BildId,
  bildMobilId: BildId,
  produkte: z.array(z.string().uuid()).max(3, 'Höchstens drei Produkte').default([]),
});
export type WerbemittelFelderEingabe = z.infer<typeof WerbemittelFelder>;

/**
 * Pflichtangaben je Art – leer, wenn alles passt. Gemeinsam für das Formular (superRefine) und das Backend
 * (beim Ändern steht die Art in der Datenbank).
 */
export function werbemittelPflichtfelder(art: WerbemittelArt, e: WerbemittelFelderEingabe): Record<string, string> {
  const f: Record<string, string> = {};
  if (!e.zielUrl) f.zielUrl = 'Bitte Ziel angeben';
  if (art === 'marke') {
    if (!e.logoBildId) f.logoBildId = 'Bitte Logo hochladen';
    if (!e.headline) f.headline = 'Bitte Headline angeben';
  }
  if (art === 'display') {
    if (!e.bildDesktopId) f.bildDesktopId = 'Bitte Bild für Desktop hochladen';
    if (!e.bildMobilId) f.bildMobilId = 'Bitte Bild für Mobil hochladen';
  }
  const max = WERBEMITTEL_GRENZEN.produkte[art];
  if (new Set(e.produkte).size > max) f.produkte = max === 0 ? 'Display-Banner enthalten keine Produkte' : `Höchstens ${max} Produkte`;
  return f;
}

function pflichtPruefen(e: WerbemittelFelderEingabe & { art: WerbemittelArt }, ctx: z.RefinementCtx) {
  for (const [feld, message] of Object.entries(werbemittelPflichtfelder(e.art, e))) ctx.addIssue({ code: 'custom', path: [feld], message });
}

/** Werbetreibender (Portal): Werbemittel für das eigene Werbekonto */
export const WerbemittelAnlegenSchema = WerbemittelFelder.extend({ art: z.enum(ARTEN) }).superRefine(pflichtPruefen);
export type WerbemittelAnlegen = z.infer<typeof WerbemittelAnlegenSchema>;

/** Marktplatz (Konto): Werbemittel für ein Werbekonto anlegen */
export const KontoWerbemittelAnlegenSchema = WerbemittelFelder.extend({ art: z.enum(ARTEN), werbekontoId: z.string().uuid() }).superRefine(pflichtPruefen);
export type KontoWerbemittelAnlegen = z.infer<typeof KontoWerbemittelAnlegenSchema>;

/** Ändern: Die Art bleibt; Pflichtangaben prüft das Backend mit der gespeicherten Art */
export const WerbemittelAendernSchema = WerbemittelFelder;
export type WerbemittelAendern = z.infer<typeof WerbemittelAendernSchema>;

export const WerbemittelAblehnenSchema = z.object({
  grund: z.string().trim().min(3, 'Bitte Grund angeben').max(WERBEMITTEL_GRENZEN.grund, `Höchstens ${WERBEMITTEL_GRENZEN.grund} Zeichen`),
});
export type WerbemittelAblehnen = z.infer<typeof WerbemittelAblehnenSchema>;

export const WerbemittelAbfrage = z.object({
  cursor: z.string().max(400).optional(),
  status: z.enum(STATUS).optional(),
  art: z.enum(ARTEN).optional(),
  /** nur im Konto: Werbemittel eines Werbekontos */
  werbekonto: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type WerbemittelAbfrageEingabe = z.infer<typeof WerbemittelAbfrage>;

export const WerbungBildVerwendungSchema = z.object({
  verwendung: z.enum(Object.keys(WERBUNG_BILD_VERWENDUNGEN) as [WerbungBildVerwendung, ...WerbungBildVerwendung[]]),
});

/* ------------------------------------------------------------------ Antworten */

export interface WerbemittelProdukt {
  id: string;
  titel: string;
  bild: string | null;
  /** Produkt steht im Shop (freigegeben/gelistet) */
  ausspielbar: boolean;
}

export interface Werbemittel {
  id: string;
  werbekontoId: string;
  werbekonto: string;
  art: WerbemittelArt;
  name: string;
  headline: string;
  text: string;
  cta: string;
  zielUrl: string;
  logoBildId: string | null;
  bildDesktopId: string | null;
  bildMobilId: string | null;
  produkte: WerbemittelProdukt[];
  status: WerbemittelStatus;
  ablehnungsgrund: string;
  /** werbetreibender: vom Marktplatz für eine WKZ-Buchung gestaltet – nur der Werbetreibende gibt frei, nur der Marktplatz ändert */
  freigabeDurch: WerbemittelFreigabeDurch;
  eingereichtAm: string | null;
  geprueftAm: string | null;
  /** in so vielen Anzeigengruppen verwendet – dann nicht löschbar */
  verwendet: number;
  erstelltAm: string;
  geaendertAm: string;
}

export interface WerbemittelSeite {
  eintraege: Werbemittel[];
  weiter: string | null;
}

/** Werbemittel an einer Anzeigengruppe */
export interface WerbemittelKurz {
  id: string;
  name: string;
  art: WerbemittelArt;
  status: WerbemittelStatus;
}

export interface WerbungBildAntwort {
  id: string;
  /** öffentlicher Pfad – liefert erst, wenn das Werbemittel eingereicht oder freigegeben ist; vorher die Vorschau-Route */
  url: string;
  mime: (typeof WERBUNG_BILD_TYPEN)[number];
  breite: number;
  hoehe: number;
}
